import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { attachAuthInterceptors } from '@/services/auth';

const GRID_FIRST = 9;
/** This app's product key in the Accounts catalog — used to mark the current tile. */
const HOST_PRODUCT_KEY = 'calendar';

const AUTH_API_BASE_URL = String(import.meta.env.VITE_AUTH_API_BASE_URL || '').replace(/\/+$/, '');
const AUTH_FRONTEND_URL = String(import.meta.env.VITE_ACCOUNTS_APP_URL || '').replace(/\/+$/, '');
const ACCOUNTS_PRODUCTS_AUDIENCE = String(import.meta.env.VITE_ACCOUNTS_AUTH_AUDIENCE || 'accounts-api');

const colorPairs = [
  { bg: 'bg-sky-50 dark:bg-sky-950/40', text: 'text-sky-600 dark:text-sky-400', ring: 'ring-sky-200/80 dark:ring-sky-800' },
  { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-600 dark:text-emerald-400', ring: 'ring-emerald-200/80 dark:ring-emerald-800' },
  { bg: 'bg-violet-50 dark:bg-violet-950/40', text: 'text-violet-600 dark:text-violet-400', ring: 'ring-violet-200/80 dark:ring-violet-800' },
  { bg: 'bg-amber-50 dark:bg-amber-950/40', text: 'text-amber-600 dark:text-amber-400', ring: 'ring-amber-200/80 dark:ring-amber-800' },
  { bg: 'bg-rose-50 dark:bg-rose-950/40', text: 'text-rose-600 dark:text-rose-400', ring: 'ring-rose-200/80 dark:ring-rose-800' },
  { bg: 'bg-cyan-50 dark:bg-cyan-950/40', text: 'text-cyan-600 dark:text-cyan-400', ring: 'ring-cyan-200/80 dark:ring-cyan-800' },
  { bg: 'bg-orange-50 dark:bg-orange-950/40', text: 'text-orange-600 dark:text-orange-400', ring: 'ring-orange-200/80 dark:ring-orange-800' },
  { bg: 'bg-indigo-50 dark:bg-indigo-950/40', text: 'text-indigo-600 dark:text-indigo-400', ring: 'ring-indigo-200/80 dark:ring-indigo-800' },
];

const pickColor = (key: string) => {
  let sum = 0;
  for (let i = 0; i < key.length; i++) sum = (sum + key.charCodeAt(i)) % 997;
  return colorPairs[sum % colorPairs.length];
};

const getInitials = (name?: string | null) => {
  const v = String(name ?? '').trim();
  if (!v) return '?';
  const parts = v.split(/\s+/g).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join('') || v[0]?.toUpperCase() || '?';
};

const ProductLogo: React.FC<{
  logoImage?: string | null;
  name?: string | null;
  imgClassName: string;
  textClassName: string;
}> = ({ logoImage, name, imgClassName, textClassName }) => {
  const [failed, setFailed] = React.useState(false);
  if (logoImage && !failed) {
    return <img src={logoImage} alt={name ?? ''} className={imgClassName} onError={() => setFailed(true)} />;
  }
  return <span className={textClassName}>{getInitials(name)}</span>;
};

type Product = {
  id: string;
  key: string;
  name: string;
  url?: string | null;
  logoImage?: string | null;
  description?: string | null;
};

type TileProduct = {
  id: string;
  name: string;
  key: string;
  url?: string;
  logoImage?: string | null;
  isCurrent?: boolean;
  isActive?: boolean;
  status?: string;
};

type MyProductsResponse = {
  organizationId?: string;
  items: Product[];
};

type OrgProductsResponse = {
  items: Array<{
    productId: string;
    organizationId: string;
    status: string;
    plan?: string | null;
    product: Product;
  }>;
};

/**
 * A tile is launchable only when BOTH the global product flag (isActive) and this
 * org's subscription (status) pass. Checking isActive must not short-circuit past
 * the status check, or an org-DISABLED product would stay enabled.
 */
const isProductEnabled = (product: TileProduct): boolean => {
  if (product.isCurrent) return true;
  if (product.isActive === false) return false;
  const status = String(product.status ?? '').toUpperCase();
  if (status === 'INACTIVE' || status === 'DISABLED') return false;
  return true;
};

const isSameOrigin = (url: string) => {
  try {
    return new URL(url, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
};

const GridIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 20 20" className={className} fill="currentColor" aria-hidden="true">
    <path d="M3 3h4v4H3V3zm5 0h4v4H8V3zm5 0h4v4h-4V3zM3 8h4v4H3V8zm5 0h4v4H8V8zm5 0h4v4h-4V8zM3 13h4v4H3v-4zm5 0h4v4H8v-4zm5 0h4v4h-4v-4z" />
  </svg>
);

export const ProductSwitcher: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [myProducts, setMyProducts] = useState<Product[]>([]);
  const [orgProducts, setOrgProducts] = useState<Array<{ id: string; status?: string; isActive?: boolean }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) setSearch('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const TEN_MINUTES = 10 * 60 * 1000;
    const isExpired = lastUpdated && Date.now() - lastUpdated > TEN_MINUTES;
    if ((lastUpdated && !isExpired) || isLoading) return;

    const fetchProducts = async () => {
      try {
        setIsLoading(true);

        // Single axios client with auth interceptors — Bearer token is added from
        // sessionStorage on each request; refresh is called only if a 401 comes back.
        const client = axios.create({ baseURL: AUTH_API_BASE_URL, withCredentials: true });
        attachAuthInterceptors(client, { audience: ACCOUNTS_PRODUCTS_AUDIENCE });

        const [mine, orgs] = await Promise.all([
          client.get('/me/products').then((r) => r.data as MyProductsResponse),
          client.get('/me/organization/products').then((r) => r.data as OrgProductsResponse),
        ]);

        const flattenedOrgs = (orgs?.items ?? []).map((item) => ({
          id: item.product.id,
          status: item.status,
          isActive: (item.product as { isActive?: boolean }).isActive,
        }));

        setMyProducts(mine?.items ?? []);
        setOrgProducts(flattenedOrgs);
        setLastUpdated(Date.now());
      } catch {
        setLastUpdated(Date.now());
      } finally {
        setIsLoading(false);
      }
    };

    void fetchProducts();
  }, [isOpen, isLoading, lastUpdated]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const handleSwitch = (product: TileProduct) => {
    if (!isProductEnabled(product)) return;
    const href = String(product.url || '').trim();
    setIsOpen(false);
    if (!href || product.isCurrent) return;
    if (isSameOrigin(href)) {
      window.location.assign(href);
    } else {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const allProducts = useMemo<TileProduct[]>(() => {
    const accounts: TileProduct = {
      id: 'accounts-internal',
      name: 'Accounts',
      key: 'accounts',
      url: `${AUTH_FRONTEND_URL}/profile`,
      logoImage: '/accounts-logo.png',
      isCurrent: false,
      isActive: true,
    };

    // /me/products may not carry status/isActive — prefer org subscription metadata.
    const orgMetaById = new Map(orgProducts.map((p) => [p.id, p]));

    const rest: TileProduct[] = myProducts
      .filter((p) => String(p.key || '').toLowerCase() !== 'accounts')
      .map((p) => {
        const orgMeta = orgMetaById.get(p.id);
        const isHost = String(p.key || '').toLowerCase() === HOST_PRODUCT_KEY;
        return {
          id: p.id,
          name: isHost ? 'Calendar' : p.name ?? p.key ?? 'App',
          key: p.key ?? p.id,
          url: isHost ? '/' : p.url ?? undefined,
          logoImage: isHost ? '/calendar-logo.png' : p.logoImage,
          isCurrent: isHost,
          isActive: orgMeta?.isActive ?? (p as TileProduct).isActive,
          status: orgMeta?.status ?? (p as TileProduct).status,
        };
      });

    return [accounts, ...rest].sort((a, b) => {
      if (a.id === 'accounts-internal') return -1;
      if (b.id === 'accounts-internal') return 1;
      if (a.isCurrent && !b.isCurrent) return -1;
      if (!a.isCurrent && b.isCurrent) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [myProducts, orgProducts]);

  const filtered = useMemo(() => {
    if (!search.trim()) return allProducts;
    const q = search.toLowerCase();
    return allProducts.filter(
      (p) => p.name?.toLowerCase().includes(q) || p.key?.toLowerCase().includes(q),
    );
  }, [allProducts, search]);

  const gridTiles = filtered.slice(0, GRID_FIRST);
  const listTiles = filtered.slice(GRID_FIRST);
  const hasSubscribedProducts = myProducts.length > 0;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex h-10 w-10 items-center justify-center rounded-2xl text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        title="Applications"
        aria-label="Applications"
      >
        <GridIcon className="h-5 w-5" />
      </button>

      {isOpen &&
        createPortal(
          <>
            <button
              type="button"
              className="fixed inset-0 z-[10050] cursor-default border-0 bg-black/45 p-0"
              aria-label="Close app switcher"
              onClick={() => setIsOpen(false)}
            />
            <div
              className="fixed inset-y-0 right-0 z-[10060] flex w-full max-w-[400px] flex-col border-l border-border bg-card shadow-2xl animate-in slide-in-from-right duration-300"
              role="dialog"
              aria-modal="true"
              aria-labelledby="product-switcher-title"
            >
              <div className="border-b border-border px-5 pb-4 pt-6">
                <h2
                  id="product-switcher-title"
                  className="mb-4 text-[15px] font-semibold tracking-tight text-foreground"
                >
                  Applications
                </h2>
                <div className="relative">
                  <svg className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><circle cx="11" cy="11" r="8" strokeWidth="2"/><path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round"/></svg>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search Applications"
                    className="h-10 w-full rounded-full border-0 bg-secondary pl-11 pr-4 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-4">
                {isLoading && myProducts.length === 0 ? (
                  <div className="flex justify-center py-20">
                    <svg className="h-6 w-6 animate-spin text-primary" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                  </div>
                ) : !hasSubscribedProducts ? (
                  <div className="px-3 py-14 text-center">
                    <p className="text-[13px] font-semibold text-foreground">No products available</p>
                    <p className="mt-1 text-[12px] text-muted-foreground">
                      Go to the Plans page and choose a plan to enable products for your organization.
                    </p>
                    <a
                      href={`${AUTH_FRONTEND_URL}/plans`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsOpen(false)}
                      className="mx-auto mt-4 inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-primary px-4 text-[12px] font-semibold text-primary-foreground transition-colors hover:opacity-90"
                    >
                      Go to Plans
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
                    </a>
                  </div>
                ) : filtered.length === 0 ? (
                  <p className="py-16 text-center text-[13px] text-muted-foreground">
                    No applications found
                  </p>
                ) : (
                  <>
                    <p className="mb-3 px-1 text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                      Your applications
                    </p>
                    <div className="mb-6 grid grid-cols-3 gap-2 sm:gap-3">
                      {gridTiles.map((product) => {
                        const color = pickColor(product.key || product.name || product.id);
                        const hasLogo = Boolean(product.logoImage);
                        const isCurrent = product.isCurrent;
                        const isEnabled = isProductEnabled(product);

                        const iconBox = (
                          <div
                            className={`mb-1 flex h-14 w-14 items-center justify-center rounded-2xl ring-2 ring-offset-2 ring-offset-card transition-transform duration-200 ${hasLogo ? 'bg-white ring-black/5' : `${color.bg} ${color.ring}`} ${!isCurrent && isEnabled ? 'group-hover:scale-[1.03]' : ''} ${!isEnabled ? 'opacity-60 grayscale' : ''}`}
                          >
                            <ProductLogo
                              logoImage={product.logoImage}
                              name={product.name}
                              imgClassName="h-11 w-11 rounded-xl object-contain p-0.5"
                              textClassName={`text-lg font-bold tracking-tight ${color.text}`}
                            />
                          </div>
                        );

                        if (isCurrent) {
                          return (
                            <div
                              key={product.id}
                              className="flex flex-col items-center rounded-2xl bg-primary/5 p-2 text-center ring-1 ring-primary/20"
                            >
                              {iconBox}
                              <span className="line-clamp-2 px-0.5 text-[11px] font-medium leading-snug text-foreground">
                                {product.name}
                              </span>
                              <span className="mt-0.5 text-[9px] font-semibold text-primary">
                                Active
                              </span>
                            </div>
                          );
                        }

                        return (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => handleSwitch(product)}
                            disabled={!isEnabled}
                            className={`group flex flex-col items-center rounded-2xl p-2 text-center transition-colors ${isEnabled ? 'hover:bg-secondary' : 'cursor-not-allowed opacity-70'}`}
                          >
                            {iconBox}
                            <span className={`line-clamp-2 px-0.5 text-[11px] font-medium leading-snug text-muted-foreground ${isEnabled ? 'group-hover:text-foreground' : ''}`}>
                              {product.name}
                            </span>
                            {!isEnabled && <span className="mt-0.5 text-[9px] font-semibold text-muted-foreground">Disabled</span>}
                          </button>
                        );
                      })}
                    </div>

                    {listTiles.length > 0 && (
                      <>
                        <p className="mb-2 mt-2 px-1 text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                          More applications
                        </p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {listTiles.map((product) => {
                            const color = pickColor(product.key || product.name || product.id);
                            const hasLogo = Boolean(product.logoImage);
                            const isCurrent = product.isCurrent;
                            const isEnabled = isProductEnabled(product);

                            const row = (
                              <div className="flex w-full min-w-0 items-center gap-2.5">
                                <div
                                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1 ${hasLogo ? 'bg-white ring-black/5' : `${color.bg} ${color.ring}`} ${!isEnabled ? 'opacity-60 grayscale' : ''}`}
                                >
                                  <ProductLogo
                                    logoImage={product.logoImage}
                                    name={product.name}
                                    imgClassName="h-7 w-7 rounded-lg object-contain p-0.5"
                                    textClassName={`text-xs font-bold ${color.text}`}
                                  />
                                </div>
                                <span className="truncate text-[12px] font-medium text-foreground">
                                  {product.name}
                                </span>
                                {isCurrent && (
                                  <span className="shrink-0 text-[9px] font-semibold text-primary">
                                    Active
                                  </span>
                                )}
                                {!isCurrent && !isEnabled && (
                                  <span className="shrink-0 text-[9px] font-semibold text-muted-foreground">Disabled</span>
                                )}
                              </div>
                            );

                            if (isCurrent) {
                              return (
                                <div
                                  key={product.id}
                                  className="flex items-center rounded-xl bg-primary/5 p-2 ring-1 ring-primary/10"
                                >
                                  {row}
                                </div>
                              );
                            }

                            return (
                              <button
                                key={product.id}
                                type="button"
                                onClick={() => handleSwitch(product)}
                                disabled={!isEnabled}
                                className={`group flex items-center rounded-xl p-2 text-left transition-colors ${isEnabled ? 'hover:bg-secondary' : 'cursor-not-allowed opacity-70'}`}
                              >
                                {row}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};
